import { Play } from 'lucide-react';
import type { PostMedia as PostMediaItem } from '@bb/shared';
import { cn } from '@/lib/cn';

/**
 * Media is displayed straight from the X CDN — nothing is downloaded or stored,
 * so a deleted post's images simply stop resolving. `onError` hides those
 * rather than leaving a broken-image box in the feed.
 */
export function PostMedia({ media }: { media: PostMediaItem[] }) {
  if (media.length === 0) return null;

  return (
    <div
      className={cn(
        // Full card width now, but still height-capped: a tall image would
        // otherwise push the metrics and actions far below the text.
        'mt-2.5 grid gap-px overflow-hidden rounded-sharp border border-line bg-line',
        media.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
      )}
    >
      {media.slice(0, 4).map((item, index) => (
        <a
          key={`${item.url}-${index}`}
          href={item.url}
          target="_blank"
          rel="noreferrer noopener"
          className="group relative block bg-bg"
        >
          <img
            src={item.previewUrl ?? item.url}
            alt={item.altText ?? ''}
            loading="lazy"
            onError={(event) => {
              const figure = event.currentTarget.closest('a');
              if (figure) figure.style.display = 'none';
            }}
            className={cn(
              'w-full object-cover transition-opacity group-hover:opacity-85',
              media.length === 1 ? 'max-h-[190px]' : 'h-24',
            )}
          />
          {item.type !== 'photo' ? (
            <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-sharp bg-bg/85 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-ink-dim">
              <Play size={9} strokeWidth={2} />
              {item.type === 'animated_gif' ? 'GIF' : 'Video'}
            </span>
          ) : null}
        </a>
      ))}
    </div>
  );
}
